/**
 * In-progress fill-in values, kept for THIS TAB only.
 *
 * These are client names, FIR numbers and facts, so they live in sessionStorage
 * (gone when the tab closes), never localStorage. Keyed per template + matter,
 * restored on return, filtered to the variables the template still declares,
 * cleared after Generate and on every sign-out. Storage can be blocked or hold
 * garbage: every call swallows its own failure and the screen works without it.
 */
const PREFIX = 'nb_draft:';

const keyOf = (templateId: string | number, matterId: number | null): string =>
  `${PREFIX}${templateId}:${matterId ?? 'none'}`;

export function loadEntries(
  templateId: string | number,
  matterId: number | null,
  variableNames: string[],
): Record<string, string> {
  try {
    const raw = sessionStorage.getItem(keyOf(templateId, matterId));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const known = new Set(variableNames);
    const out: Record<string, string> = {};
    for (const [name, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (known.has(name) && typeof value === 'string' && value.trim() !== '') out[name] = value;
    }
    return out;
  } catch {
    return {};
  }
}

export function saveEntries(
  templateId: string | number,
  matterId: number | null,
  values: Record<string, string>,
): void {
  try {
    // Only what was typed: an empty field saved here would later beat a value the matter gained since.
    const typed = Object.fromEntries(Object.entries(values).filter(([, v]) => (v ?? '').trim() !== ''));
    sessionStorage.setItem(keyOf(templateId, matterId), JSON.stringify(typed));
  } catch {
    /* storage blocked or full — the form still works */
  }
}

export function clearEntries(templateId: string | number, matterId: number | null): void {
  try {
    sessionStorage.removeItem(keyOf(templateId, matterId));
  } catch {
    /* nothing to clear */
  }
}

export function clearAllDraftEntries(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(PREFIX)) keys.push(k);
    }
    keys.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* nothing to clear */
  }
}
